import { flush, injectGlobal } from '@emotion/css';
import { createTheme, Theme, ThemeProvider } from '@mui/material/styles';
import { Fragment, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, Route, useLocation, useParams } from 'react-router-dom';
import RoutesJson from 'shared/configs/route-items';
import { getDesignTokens } from 'shared/configs/theme';
import { DashboardLayout, NormalLayout } from 'shared/layout';
import { useAppSelector } from 'shared/hooks';
import { ClyCoreConfig } from '@coinlocally/cly-core';

const RequireAuth: FC<{ children: ReactNode }> = ({ children }) => {
	const isLogin = useAppSelector(state => state.Profile.isLogin);
	const location = useLocation();

	const language = useTranslation().i18n.language;

	return <Fragment>{isLogin ? children : <Navigate to={`/${language}/login?callbackUrl=${location.pathname}`} replace />}</Fragment>;
};

const RequireNoAuth: FC<{ children: ReactNode }> = ({ children }) => {
	const isLogin = useAppSelector(state => state.Profile.isLogin);
	const location = useLocation();

	const { i18n } = useTranslation();
	let language = i18n.language;

	return (
		<Fragment>{!isLogin ? children : <Navigate to={`/${language}/dashboard`} replace state={{ path: location.pathname }} />}</Fragment>
	);
};

export const Router = () => {
	const RoutesJSON = RoutesJson();
	return RoutesJSON.map((item, index) => {
		const Element =
			item.layout.type === 'normal' ? (
				<NormalLayout
					navbarDisplay={item.layout.navbarDisplay}
					footerDisplay={item.layout.footerDisplay}
					navbarType={item.layout.navbarType}
					showTopNavBar={item.layout.showTopNavBar}
				>
					<item.component />
				</NormalLayout>
			) : (
				<DashboardLayout footerDisplay={item.layout.footerDisplay} dashboardType={item.layout.dashboardType}>
					<item.component />
				</DashboardLayout>
			);

		return item.authType === 'guardless' ? (
			<Route path={item.route} element={Element} key={'route' + index} />
		) : item.authType === 'auth' ? (
			<Route path={item.route} element={<RequireAuth>{Element}</RequireAuth>} key={'route' + index} />
		) : (
			<Route path={item.route} element={<RequireNoAuth>{Element}</RequireNoAuth>} key={'route' + index} />
		);
	});
};

export const ThemeHandler: FC<{ children: ReactNode }> = ({ children }) => {
	const language = useTranslation().i18n.language;
	const direction = language === 'fa' || language === 'ar' ? 'rtl' : 'ltr';
	// const direction = 'ltr';

	const [themeToken, setThemeToken] = useState<Theme>();
	useEffect(() => {
		const token = getDesignTokens('light', direction, language);
		document.dir = direction;
		setThemeToken(createTheme(token));
		document.querySelector('body')!.setAttribute('data-theme', 'light');

		injectGlobal`
			body{
				direction: ${direction};
				font-family: ${direction === 'ltr' ? 'var(--font-product-sans)' : 'YekanBakh'} !important;
				overflow-x: hidden;
			}
			body * {
				font-family: ${direction === 'ltr' ? 'var(--font-product-sans)' : 'YekanBakh'} !important;
			}
			input {
				&:-webkit-autofill,
				:-webkit-autofill:hover,
				:-webkit-autofill:focus {
					-webkit-text-fill-color: ${token.mode === 'dark' ? 'white' : 'black'};
					box-shadow: 0 0 0px 40rem ${token.colors.background.input.primary} inset;
					caret-color: ${token.mode === 'dark' ? 'white' : 'black'};
					border: 9px solid ${token.colors.background.input.primary} !important;
					padding: 0 10px !important;
				}
			}
			.arrow, .ant-picker-range-separator{
				transform: ${direction === 'ltr' ? 'rotate(0)' : 'rotate(180deg)'};
			}
		`;
		return () => flush();
	}, [language]);

	if (themeToken) {
		return <ThemeProvider theme={themeToken}>{children}</ThemeProvider>;
	}

	return <Outlet />;
};

export function useTranslationNamespace(namespace: string, resources: { [lng: string]: any }) {
	const { t, i18n } = useTranslation();
	const [ready, setReady] = useState(false);

	useEffect(() => {
		Object.entries(resources).forEach(([lng, res]) => {
			i18n.addResourceBundle(lng, namespace, res, true, true);
		});

		i18n.loadNamespaces(namespace, () => {
			setReady(true);
		});
	}, [i18n, namespace, resources]);

	return { t, ready };
}

export const LanguageHandler = () => {
	const { lng } = useParams();
	const { i18n } = useTranslation();
	const location = useLocation();
	let language = i18n.language;
	let languages = ['en', 'fa', 'ru', 'fr', 'de', 'es'];
	// let languages = ['en'];

	if (i18n.language.includes('-')) {
		language = i18n.language.split('-')[0];
	}

	useEffect(() => {
		if (!lng || lng.length !== 2) {
			i18n.changeLanguage('en');
			ClyCoreConfig.LANGUAGE = 'en' as 'en' | 'fa' | 'ru' | 'ar' | 'de' | 'es';
			//ClyCoreConfig.LANGUAGE = 'en';
			window.location.replace(language + location.pathname);
		}
	}, [location.pathname]);

	useEffect(() => {
		if (lng && lng.length === 2 && lng !== i18n.language) {
			if (languages.includes(lng)) {
				i18n.changeLanguage(lng);
				ClyCoreConfig.LANGUAGE = lng as 'en' | 'fa' | 'ru' | 'ar' | 'de' | 'es';
				//ClyCoreConfig.LANGUAGE = lng as 'en';
			} else {
				window.location.replace(location.pathname.replace(lng, language));
			}
		}
	}, [lng]);

	return <Outlet />;
};
